import { ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center pt-48 sm:pt-52 pb-20 overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* Background layers */}
      <div className="absolute inset-0 z-0">
        <img
          src={BRAND.images.hero}
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/60 to-[#050505]" />
        <div
          className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full blur-[120px] opacity-30"
          style={{ background: "#E91E8C" }}
        />
        <div
          className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-[140px] opacity-20"
          style={{ background: "#FF6AC1" }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 space-y-8">
          <div
            className="reveal flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-white/70"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-brand" />
            <MapPin className="w-3.5 h-3.5 text-brand" />
            Houston, Texas · Est. {BRAND.mcEst}
          </div>

          <h1
            className="reveal reveal-delay-1 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-serif leading-[0.95] tracking-tight"
            data-testid="hero-title"
          >
            Where Queens
            <br />
            <span className="font-script text-brand text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
              ride & reign.
            </span>
          </h1>

          <p
            className="reveal reveal-delay-2 max-w-xl text-lg text-white/75 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Crown Jewels Sisterhood — Houston-rooted, nationally ridden. A
            women-led Motorcycle Club and Social Club of fearless sisters
            bonded across states, 2s and 3s, and one unshakeable purpose.
          </p>

          <p
            className="reveal reveal-delay-2 font-script text-3xl sm:text-4xl text-brand leading-tight max-w-xl"
            data-testid="hero-motto"
          >
            "Like precious jewels,
            <br />
            our sisterhood is rare."
          </p>

          <div
            className="reveal reveal-delay-3 flex flex-col sm:flex-row gap-4"
            data-testid="hero-cta-group"
          >
            <Button
              asChild
              className="bg-brand hover:bg-brand-hover text-white rounded-none px-8 h-14 text-sm font-semibold tracking-[0.15em] uppercase shadow-brand group"
              data-testid="hero-cta-join"
            >
              <a href="#join">
                Join the Sisterhood
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <Button
              asChild
              variant="ghost"
              className="text-white border border-white/20 hover:border-brand hover:text-brand rounded-none px-8 h-14 text-sm font-semibold tracking-[0.15em] uppercase bg-transparent hover:bg-transparent"
              data-testid="hero-cta-story"
            >
              <a href="#about">Our Story</a>
            </Button>
          </div>

          {/* Stats */}
          <div
            className="reveal reveal-delay-4 grid grid-cols-3 gap-6 pt-10 border-t border-white/10 max-w-xl"
            data-testid="hero-stats"
          >
            {[
              { n: "5+", l: "Years Strong" },
              { n: "2", l: "Branches — MC & SC" },
              { n: "∞", l: "Sisterhood Bond" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-serif text-3xl lg:text-4xl text-white">
                  {s.n}
                </div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Patch image */}
        <div className="lg:col-span-5 relative reveal reveal-delay-2" data-testid="hero-patch">
          <div className="relative mx-auto max-w-md">
            <div
              className="absolute inset-0 blur-3xl opacity-50"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, #E91E8C 0%, transparent 60%)",
              }}
            />
            <img
              src={BRAND.patchImage}
              alt="Crown Jewels Sisterhood Houston Patches"
              className="relative w-full h-auto shadow-brand-lg"
              data-testid="hero-patch-image"
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 text-[10px] uppercase tracking-[0.3em]">
        <span>Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-brand to-transparent animate-pulse" />
      </div>
    </section>
  );
}
